import React, { useEffect, useState, useRef, useLayoutEffect } from "react";
import { useEventContext } from "../util/EventProvider";
import "./EventInfo.scss";
import Skeleton from "./Skeleton";
import EventMetadata from "../components/EventMetaData";

export function EventInfo({}) {
    const {
    fullEventId,
    customEventDetails,
    eventInstances,
    loading,
    shortenedEventId, // Correct spelling here
  } = useEventContext();

  const eventId = fullEventId;
  const [resizeKey, setResizeKey] = useState(0);

  const triggerResize = () => {
    if (typeof window.parentIFrame?.size === "function") {
      setTimeout(() => {
        window.parentIFrame.size();
        console.log("Resize triggered", new Date().toLocaleTimeString());
        setResizeKey((prev) => prev + 1); // Force re-render
      }, 300); // Longer delay for dynamic content
    } else {
      console.warn("parentIFrame not available");
    }
  };

  useEffect(() => {
    console.log("ParentIFrame available?", !!window.parentIFrame);
  }, []);

  useEffect(() => {
    const debugSpacing = () => {
      const allElements = Array.from(document.querySelectorAll("*"));
      const bottomElements = allElements
        .filter((el) => {
          const rect = el.getBoundingClientRect();
          return rect.bottom > window.innerHeight - 20;
        })
        .map((el) => {
          const rect = el.getBoundingClientRect(); // Define rect here again
          return {
            element: el.tagName,
            class: el.className,
            id: el.id,
            bottom: rect.bottom,
          };
        });

      console.table(bottomElements);
      triggerResize();
    };

    const timer = setTimeout(debugSpacing, 500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Fix common spacing offenders
    const style = document.createElement("style");
    style.textContent = `
      body > *:last-child {
        margin-bottom: 0 !important;
      }
      .event-footer, [class*="-container"] {
        padding-bottom: 0 !important;
      }
    `;
    document.head.appendChild(style);

    return () => document.head.removeChild(style);
  }, []);

  // Function to extract video ID from YouTube URL
  const extractVideoId = (videoLink) => {
    const urlParams = new URLSearchParams(videoLink.split("?")[1]);
    return urlParams.get("v");
  };

  // Function to scroll to a specific section
  function scrollToSection(sectionId) {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: "smooth" });
    }
  }

  // Navigate back to events page
  const handleNavigateBack = () => {
    window.open("https://jazzforumarts.org/events", "_top");
  };

  // Context-based event filtering
  const onSaleEvents = (customEventDetails || [])
    .filter((event) => event.isOnSale)
    .filter((event) => event.start);

  // Find the event with the matching eventId
  const selectedEvent = onSaleEvents.filter((event) => event.id === eventId);

  // Find start time for the event
  const eventDate =
    selectedEvent.length > 0 ? new Date(selectedEvent[0].start) : null;

  // Find all events with the matching eventDate
  const selectedDate = eventDate
    ? selectedEvent.filter(
        (event) =>
          new Date(event.start).toDateString() === eventDate.toDateString()
      )
    : [];

  const sortedInstances = [...eventInstances].sort(
    (a, b) => new Date(a.start) - new Date(b.start)
  );

  // 2. Content Ready Notification (when data loads)
  useEffect(() => {
    if (!loading && customEventDetails && eventId) {
      const timer = setTimeout(() => {
        window.parent.postMessage(
          {
            type: "IFRAME_READY",
            payload: {
              height: document.body.scrollHeight,
              eventId: eventId,
              contentLoaded: true,
            },
          },
          "*"
        );
      }, 100); // Shorter delay since we're using context

      return () => clearTimeout(timer);
    }
  }, [loading, customEventDetails, eventId]);

  const handleRSVPClick = () => {
    const fbLink = selectedDate[0]?.attribute_RSVPFacebookLink;
    if (fbLink) {
      window.top.open(fbLink, "_blank"); // Attempt to open in a new tab
    }
  };

  // Layout effect remains the same
  const elRef = useRef();

  useLayoutEffect(() => {
    if (elRef.current) {
      const firstChild = elRef.current.firstElementChild;
      if (firstChild) {
        firstChild.style.fontFamily = "Montserrat, Poppins, sans-serif";
        firstChild.style.textTransform = "none";
        firstChild.style.fontStyle = "normal";
        firstChild.style.color = "white";
        firstChild.style.paddingBottom = "10px";
        firstChild.style.paddingRight = "0px";
        firstChild.style.margin = "0";
        firstChild.style.fontSize = "18px";
        firstChild.style.letterSpacing = "-0.5px";
      }
    }
  }, [selectedDate]);

  return (
    <>
      <EventMetadata event={selectedEvent} />
      {loading ? (
        <div>
          <Skeleton />
        </div>
      ) : (
        <div
          className="animate-fadeIn md:ml-25 antialiased EventInfo visit-checkout-container"
          style={{
            minHeight: "auto",
            maxHeight: "100%",
            width: "100%",
            height: "100%",
            padding: "0px 10px",
            justifyContent: "flext-start",
          }}
        >
          {" "}
          {/* Ensure each key is unique */}
          <div
            style={{
              alignItems: "center",
              justifyContent: "flex-start",
              paddingBottom: "5px",
            }}
          >
            <button
              onClick={handleNavigateBack}
              style={{
                width: "auto",
                maxWidth: "100%",
                fontFamily: "Montserrat, Poppins, sans-serif",
                textTransform: "uppercase",
                //letterSpacing: "1.5px",
                fontSize: "1.2rem",
                height: "40px",
                background: "none",
                border: "none",
                borderRadius: "none",
                display: "inline-flex",
                lineHeight: "1.5rem",
                alignItems: "center",
                justifyContent: "flex-start",
                padding: "0 20px !important", // Override Elementor padding
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                color: "white",
                textDecoration: "none", // Remove default link underline
              }}
            >
              <span style={{ display: "flex", alignItems: "center" }}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 20 24"
                  strokeWidth={3}
                  stroke="currentColor"
                  className="w-3 h-3"
                  style={{ marginRight: "5px", cursor: "pointer" }}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="m18.75 4.5-7.5 7.5 7.5 7.5m-6-15L5.25 12l7.5 7.5"
                  />
                </svg>
                <span
                  className="leading-tight text-xxm sm:text-xl"
                  style={{
                    width: "auto",
                    maxWidth: "100%",
                    fontFamily: "Montserrat, Poppins, sans-serif",
                    textTransform: "uppercase",
                    fontWeight: "bold",
                  }}
                >
                  See all events
                </span>
              </span>
            </button>
          </div>
          {/* Check if there is at least one event in selectedDate */}
          {selectedDate.length > 0 && (
            <li
              className="content-center"
              key={selectedDate[0].instanceId}
              style={{
                minHeight: "200px",
                minWidth: "100%",
                maxWidth: "100%",
                listStyleType: "none",
              }}
            >
              {/*existing event instance rendering logic */}
              <div className="grid place-content-center gap-1 grid-cols-1">
                <div style={{ width: "fit-content" }}>
                  <div className="flex flex-col sm:flex-row">
                    {/* Render the event image */}
                    <div className="pb-2">
                      <img
                        src={selectedDate[0].imageUrl}
                        alt={selectedDate[0].name}
                        className="object-cover w-full h-full"
                        style={{
                          objectFit: "contain",
                          objectPosition: "top", // Position the image at the top
                        }}
                      />
                    </div>
                     <div className="flex-row sm:flex-col block sm:hidden">
                    <div className="w-full pb-10">
                      <section className="ExtraInfo">
                        {/* Render tags and event category within a single div */}
                        <div className="text-align-center">
                          {/* Conditionally render tag 1 if it's not null */}
                          {selectedDate[0].attribute_EventTag1 && (
                            <label
                              className="inline-flex justify-center items-center text-xxxs sm:text-xl"
                              style={{
                                padding: "4px",
                                fontWeight: "lighter",
                                color: "white",
                                letterSpacing: "2px",
                                lineHeight: "1",
                                fontSize: ".8rem",
                                background: "#2D2D2D",
                                fontFamily: "Montserrat, Poppins, sans-serif",
                                textTransform: "uppercase",
                                marginRight: "6px", // Add margin between tags
                              }}
                            >
                              {selectedDate[0].attribute_EventTag1}
                            </label>
                          )}
                          {/* Conditionally render tag 2 if it's not null */}
                          {selectedDate[0].attribute_EventTag2 && (
                            <label
                              className="inline-flex justify-center items-center text-xxxs sm:text-xl"
                              style={{
                                padding: "4px",
                                fontWeight: "lighter",
                                color: "white",
                                letterSpacing: "2px",
                                lineHeight: "1",
                                fontSize: ".8rem",
                                background: "#2D2D2D",
                                fontFamily: "Montserrat, Poppins, sans-serif",
                                textTransform: "uppercase",
                                marginRight: "6px", // Add margin between tags
                              }}
                            >
                              {selectedDate[0].attribute_EventTag2}
                            </label>
                          )}
                          {/* Conditionally render tag 3 if it's not null */}
                          {selectedDate[0].attribute_EventTag3 && (
                            <label
                              className="inline-flex justify-center items-center text-xxxs sm:text-xl"
                              style={{
                                padding: "4px",
                                fontWeight: "lighter",
                                color: "white",
                                letterSpacing: "2px",
                                lineHeight: "1",
                                fontSize: ".8rem",
                                background: "#2D2D2D",
                                fontFamily: "Montserrat, Poppins, sans-serif",
                                textTransform: "uppercase",
                                marginRight: "3px", // Add margin between tags
                              }}
                            >
                              {selectedDate[0].attribute_EventTag3}
                            </label>
                          )}
                          {/* Conditionally render Event Category if it's not null */}
                          {selectedDate[0].attribute_EventCategory && (
                            <p
                              className="inline-flex justify-center items-center text-xxxs sm:text-xl"
                              style={{
                                padding: "4px",
                                fontWeight: "lighter",
                                color: "white",
                                letterSpacing: "2px",
                                lineHeight: "1",
                                fontSize: ".8rem",
                                //background: "#2D2D2D",
                                fontFamily: "Montserrat, Poppins, sans-serif",
                                textTransform: "uppercase",
                                fontStyle: "italic", // Apply cursive font style
                                marginRight: "3px", // Add margin between tags
                              }}
                            >
                              {selectedDate[0].attribute_EventCategory}
                            </p>
                          )}
                        </div>
                      </section>
                    </div>
                  </div>
                    {/* Render the event information */}
                    <div className="w-full pb-2 sm:px-5">
                      <h3
                        className="text-3xl text-left"
                        style={{
                          fontFamily: "Norwester, Poppins, sans-serif",
                          textTransform: "uppercase",
                          fontStyle: "normal",
                          color: "white",
                          lineHeight: "1", // Increase the line height (leading)
                        }}
                      >
                        {selectedDate[0].name}
                      </h3>
                      <div className="text-2xl">
                        <h4
                          style={{
                            fontFamily: "Norwester, Poppins, sans-serif",
                            textTransform: "uppercase",
                            fontStyle: "normal",
                            color: "white",
                            paddingBottom: "3px",
                            margin: "0", // Add margin
                          }}
                        >
                          {selectedDate[0].attribute_Subtitle}
                        </h4>
                      </div>
                      <div className="pb-2">
                        <h1 className="text-left" style={{ margin: "0" }}>
                          <p
                            style={{
                              fontFamily: "Montserrat, Poppins, sans-serif",
                              textTransform: "none",
                              fontStyle: "normal",
                              color: "white",
                              paddingBottom: "3px",
                              fontWeight: "bold",
                              margin: "0", // Add margin
                            }}
                          >
                            {selectedDate[0].attribute_SpecialInformation}
                          </p>
                        </h1>
                      </div>
                      <div className="pb-2">
                        <h1 className="text-left" style={{ margin: "0" }}>
                          <p
                            style={{
                              fontFamily: "Montserrat, Poppins, sans-serif",
                              textTransform: "none",
                              fontStyle: "normal",
                              color: "white",
                              paddingBottom: "3px",
                              margin: "0", // Add margin
                            }}
                          >
                            {selectedDate[0].attribute_FeaturedArtists}
                          </p>
                        </h1>
                      </div>
                      {/* Event Dates Section for Desktop */}
                      <div className="hidden md:block">
                      <section className="flex flex-col sm:flex-row">
                        <div className="event-instances-by-date">
                          {sortedInstances.length > 0 ? (
                            sortedInstances.map((instance) => {
                              const date = new Date(instance.start);
                              const formatted = date.toLocaleString("en-US", {
                                weekday: "long",
                                month: "long",
                                day: "numeric",
                                hour: "numeric",
                                minute: "2-digit",
                              });

                              return (
                                <div
                                  key={instance.id}
                                  className="event-instance-date"
                                >
                                  {formatted}
                                </div>
                              );
                            })
                          ) : (
                            <p>No upcoming dates available.</p>
                          )}
                        </div>
                      </section>
                      </div>
                    </div>
                  </div>

                  <div className="flex-row sm:flex-col md:block hidden">
                    <div className="w-full pb-10">
                      <section className="ExtraInfo">
                        {/* Render tags and event category within a single div */}
                        <div className="text-align-center">
                          {/* Conditionally render tag 1 if it's not null */}
                          {selectedDate[0].attribute_EventTag1 && (
                            <label
                              className="inline-flex justify-center items-center text-xxxs sm:text-xl"
                              style={{
                                padding: "4px",
                                fontWeight: "lighter",
                                color: "white",
                                letterSpacing: "2px",
                                lineHeight: "1",
                                fontSize: ".8rem",
                                background: "#2D2D2D",
                                fontFamily: "Montserrat, Poppins, sans-serif",
                                textTransform: "uppercase",
                                marginRight: "6px", // Add margin between tags
                              }}
                            >
                              {selectedDate[0].attribute_EventTag1}
                            </label>
                          )}
                          {/* Conditionally render tag 2 if it's not null */}
                          {selectedDate[0].attribute_EventTag2 && (
                            <label
                              className="inline-flex justify-center items-center text-xxxs sm:text-xl"
                              style={{
                                padding: "4px",
                                fontWeight: "lighter",
                                color: "white",
                                letterSpacing: "2px",
                                lineHeight: "1",
                                fontSize: ".8rem",
                                background: "#2D2D2D",
                                fontFamily: "Montserrat, Poppins, sans-serif",
                                textTransform: "uppercase",
                                marginRight: "6px", // Add margin between tags
                              }}
                            >
                              {selectedDate[0].attribute_EventTag2}
                            </label>
                          )}
                          {/* Conditionally render tag 3 if it's not null */}
                          {selectedDate[0].attribute_EventTag3 && (
                            <label
                              className="inline-flex justify-center items-center text-xxxs sm:text-xl"
                              style={{
                                padding: "4px",
                                fontWeight: "lighter",
                                color: "white",
                                letterSpacing: "2px",
                                lineHeight: "1",
                                fontSize: ".8rem",
                                background: "#2D2D2D",
                                fontFamily: "Montserrat, Poppins, sans-serif",
                                textTransform: "uppercase",
                                marginRight: "3px", // Add margin between tags
                              }}
                            >
                              {selectedDate[0].attribute_EventTag3}
                            </label>
                          )}
                          {/* Conditionally render Event Category if it's not null */}
                          {selectedDate[0].attribute_EventCategory && (
                            <p
                              className="inline-flex justify-center items-center text-xxxs sm:text-xl"
                              style={{
                                padding: "4px",
                                fontWeight: "lighter",
                                color: "white",
                                letterSpacing: "2px",
                                lineHeight: "1",
                                fontSize: ".8rem",
                                //background: "#2D2D2D",
                                fontFamily: "Montserrat, Poppins, sans-serif",
                                textTransform: "uppercase",
                                fontStyle: "italic", // Apply cursive font style
                                marginRight: "3px", // Add margin between tags
                              }}
                            >
                              {selectedDate[0].attribute_EventCategory}
                            </p>
                          )}
                        </div>
                      </section>
                    </div>
                  </div>
                   {/* Event Dates Section for Mobile */}
                    <div className="block sm:hidden">
                      <section className="flex flex-col sm:flex-row">
                        <div className="event-instances-by-date">
                          {sortedInstances.length > 0 ? (
                            sortedInstances.map((instance) => {
                              const date = new Date(instance.start);
                              const formatted = date.toLocaleString("en-US", {
                                weekday: "long",
                                month: "long",
                                day: "numeric",
                                hour: "numeric",
                                minute: "2-digit",
                              });

                              return (
                                <div
                                  key={instance.id}
                                  className="event-instance-date"
                                >
                                  {formatted}
                                </div>
                              );
                            })
                          ) : (
                            <p>No upcoming dates available.</p>
                          )}
                        </div>
                      </section>
                      </div>
                </div>
              </div>
            </li>
          )}
          {/* Event Info Section */}
          <section
            className="flex flex-col sm:flex-row"
            id="EventInfo"
            style={{
              fontFamily: "Montserrat, sans-serif",
              textTransform: "none",
              fontStyle: "normal",
              color: "white",
              paddingTop: "20px",
              paddingBottom: "40px",
              margin: "0", // Add margin
            }}
          >
            {/* Event Info */}
            <div
              className="w-full"
              style={{ marginBottom: "20px", paddingRight: "20px" }}
            >
              <h4
                style={{
                  color: "white",
                  fontSize: "24px",
                  paddingBottom: "12px",
                  textTransform: "uppercase",
                  fontFamily: "Norwester",
                }}
              >
                Event Info:
              </h4>
              {selectedDate.length > 0 && (
                <div
                  ref={elRef}
                  dangerouslySetInnerHTML={{
                    __html: selectedDate[0]?.htmlDescription || "",
                  }}
                />
              )}
              {/* FB Link */}
              <section
                className="pt-5"
                style={{
                  paddingBottom: selectedDate[0]?.attribute_Website
                    ? "15px"
                    : "0px", // Conditional padding
                }}
              >
                {selectedDate[0]?.attribute_RSVPFacebookLink && (
                  <div>
                    <button
                      onClick={handleRSVPClick}
                      style={{
                        width: "auto",
                        maxWidth: "100%",
                        fontFamily: "Montserrat, Poppins, sans-serif",
                        textTransform: "uppercase",
                        letterSpacing: "2.5px",
                        fontSize: ".8rem",
                        height: "40px",
                        background: "#009ED0",
                        border: "none",
                        borderRadius: "none",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        padding: "25px",
                      }}
                    >
                      {"RSVP on Facebook"}
                    </button>
                  </div>
                )}
              </section>
              {/* Website Link */}
              <section className="pb-15">
                {selectedDate[0]?.attribute_Website && (
                  <div>
                    <a
                      href={selectedDate[0].attribute_Website}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ textDecoration: "none" }} // Optional: removes underline from the link
                    >
                      <button
                        style={{
                          width: "auto",
                          maxWidth: "100%",
                          fontFamily: "Montserrat, Poppins, sans-serif",
                          textTransform: "uppercase",
                          letterSpacing: "2.5px",
                          fontSize: ".8rem",
                          height: "40px",
                          background: "#2D2D2D",
                          border: "none",
                          borderRadius: "none",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          padding: "25px",
                        }}
                      >
                        {"View Artist Website"}
                      </button>
                    </a>
                  </div>
                )}
              </section>
            </div>
            {/* Video */}
            <div
              className="w-full"
              style={{
                fontFamily: "Montserrat, Poppins, sans-serif",
                textTransform: "none",
                fontStyle: "normal",
                color: "white",
                paddingBottom: "10px",
                margin: "0",
              }}
            >
              {selectedDate.length > 0 &&
                selectedDate[0].attribute_VideoLink && ( // Check if video link exists
                  <iframe
                    width="100%"
                    height="auto"
                    style={{
                      maxWidth: "100%",
                      aspectRatio: "16/9",
                    }}
                    src={`https://www.youtube.com/embed/${extractVideoId(
                      selectedDate[0].attribute_VideoLink
                    )}`}
                    title="YouTube video player"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                  ></iframe>
                )}
            </div>
          </section>
        </div>
      )}
    </>
  );
}
